import {
  DesktopLogo,
  DesktopTopBar,
  DriverSidebarProfile,
} from "@/components/desktop/DesktopChrome";
import { LogOutModal } from "@/components/log-out-modal";
import { LoyaltyBadges } from "@/components/loyalty-badges";
import { SwitchMode } from "@/components/switch-mode";
import { ThemedText } from "@/components/themed-text";
import { Avatar } from "@/components/ui/avatar";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { getRoleLabel } from "@/lib/roles";
import Ionicons from "@react-native-vector-icons/ionicons";
import {
  DrawerContentScrollView,
  DrawerItem,
  DrawerItemList,
} from "@react-navigation/drawer";
import { router } from "expo-router";
import { Drawer } from "expo-router/drawer";
import React, { useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";

function DrawerHeader() {
  const { user } = useAuth();

  const fullName = `${user?.first_name} ${user?.last_name}`;
  const roleLabel = getRoleLabel(user?.role);
  const roleColor = user?.role === "driver" ? "#10b981" : "#3b82f6";

  return (
    <TouchableOpacity
      style={styles.profileHeader}
      onPress={() => router.push("/(tabs)/profile")}
      activeOpacity={0.8}
      testID="profile_header"
    >
      <Avatar src={user?.avatar} name={fullName} />
      <View style={styles.profileInfo}>
        <ThemedText style={styles.profileName} numberOfLines={1}>
          {fullName}
        </ThemedText>
        <View style={[styles.roleBadge, { backgroundColor: roleColor }]}>
          <ThemedText style={styles.roleText}>
            {roleLabel?.replace("Business", "")}
          </ThemedText>
        </View>
        <View style={styles.ratingRow}>
          <Ionicons name="star" size={14} color="#facc15" />
          <ThemedText style={styles.ratingValue}>
            {user?.reputation_score} rating
            {user?.total_ratings_count === 1 ? "" : "s"}
          </ThemedText>
        </View>
        {user?.loyalty_tier || user?.is_top_rated ? (
          <View style={styles.achievementBadges}>
            <LoyaltyBadges
              loyaltyTier={user?.loyalty_tier}
              isTopRated={user?.is_top_rated}
            />
          </View>
        ) : null}
      </View>
      <Ionicons name="chevron-forward" size={20} color="#94a3b8" />
    </TouchableOpacity>
  );
}

function CustomDrawerContent(props: any) {
  const { user, signOut, theme } = useAuth();
  const desktop = useDesktopLayout();
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = () => {
    setLogoutModalVisible(true);
  };

  const handleCancelLogout = () => {
    if (loggingOut) {
      return;
    }

    setLogoutModalVisible(false);
  };

  const handleConfirmLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      setLogoutModalVisible(false);
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <>
      <DrawerContentScrollView
        {...props}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ flex: 1, backgroundColor: theme.background }}
      >
        {desktop ? <DesktopLogo /> : <DrawerHeader />}
        {desktop && user?.role === "driver" ? <DriverSidebarProfile /> : null}
        <DrawerItemList
          {...props}
          labelStyle={{ fontSize: 16, color: theme.text }}
        />
        <View style={{ flex: 1 }} />
        <View style={{ paddingBottom: 20 }}>
          <DrawerItem
            label="Log Out"
            icon={({ size }) => (
              <Ionicons
                name="log-out-outline"
                size={size}
                color={desktop ? BrandColors.error : theme.text}
              />
            )}
            onPress={handleLogout}
            labelStyle={{
              fontSize: 16,
              color: desktop ? BrandColors.error : theme.text,
            }}
            activeTintColor={BrandColors.primary}
          />
        </View>
        <SwitchMode />
      </DrawerContentScrollView>

      <LogOutModal
        visible={logoutModalVisible}
        onCancel={handleCancelLogout}
        onConfirm={handleConfirmLogout}
        loading={loggingOut}
      />
    </>
  );
}

export default function DrawerLayout() {
  const { user, theme } = useAuth();
  const isDriver = user?.role === "driver";
  const desktop = useDesktopLayout();
  const desktopItemStyle = desktop
    ? { borderRadius: 12, marginHorizontal: 8, marginVertical: 2 }
    : undefined;

  return (
    <Drawer
      drawerContent={(props) => <CustomDrawerContent {...props} />}
      screenLayout={({ children }) =>
        desktop ? (
          <View style={styles.desktopScene}>
            <DesktopTopBar />
            <View style={styles.desktopSceneBody}>{children}</View>
          </View>
        ) : (
          children
        )
      }
      screenOptions={{
        drawerType: desktop ? "permanent" : "front",
        swipeEnabled: !desktop,
        drawerActiveTintColor: BrandColors.primary,
        drawerInactiveTintColor: desktop ? theme.icon : theme.text,
        drawerActiveBackgroundColor: desktop
          ? "rgba(15, 185, 177, 0.12)"
          : undefined,
        drawerStyle: desktop
          ? {
              backgroundColor: theme.background,
              borderRightWidth: 1,
              borderRightColor: theme.border,
            }
          : undefined,
        drawerContentStyle: { backgroundColor: theme.background },
        drawerItemStyle: desktopItemStyle,
        drawerLabelStyle: { fontSize: 16, color: theme.text },
        headerShown: false,
      }}
    >
      <Drawer.Screen
        name="index"
        options={{
          title: "Home",
          drawerLabel: desktop
            ? ({ color, focused }) => (
                <View style={styles.homeLabel}>
                  <Text
                    style={{
                      color,
                      fontSize: 16,
                      fontWeight: focused ? "600" : "400",
                    }}
                  >
                    Home
                  </Text>
                </View>
              )
            : "Home",
          drawerIcon: ({ color, size }) => (
            <Ionicons name="home" size={size} color={color} />
          ),
        }}
      />
      <Drawer.Screen
        name="bookings"
        options={{
          title: "Bookings",
          drawerIcon: ({ color, size }) => (
            <Ionicons name="calendar" size={size} color={color} />
          ),
        }}
      />
      <Drawer.Screen
        name="scheduled-jobs"
        options={{
          title: "Scheduled Jobs",
          drawerIcon: ({ color, size }) => (
            <Ionicons name="time" size={size} color={color} />
          ),
          drawerItemStyle: {
            display: isDriver ? "flex" : "none",
            ...desktopItemStyle,
          },
        }}
      />
      <Drawer.Screen
        name="vehicles"
        options={{
          title: "My Vehicles",
          drawerIcon: ({ color, size }) => (
            <Ionicons name="car-sport" size={size} color={color} />
          ),
          drawerItemStyle: {
            display: isDriver ? "flex" : "none",
            ...desktopItemStyle,
          },
        }}
      />
      <Drawer.Screen
        name="wallet"
        options={{
          title: "Wallet",
          drawerIcon: ({ color, size }) => (
            <Ionicons name="wallet" size={size} color={color} />
          ),
          drawerItemStyle: {
            display: isDriver ? "flex" : "none",
            ...desktopItemStyle,
          },
        }}
      />
      <Drawer.Screen
        name="leaderboard"
        options={{
          title: "Leaderboard",
          drawerIcon: ({ color, size }) => (
            <Ionicons name="trophy" size={size} color={color} />
          ),
          drawerItemStyle: {
            display: isDriver ? "flex" : "none",
            ...desktopItemStyle,
          },
        }}
      />
      <Drawer.Screen
        name="rewards"
        options={{
          title: "Reputation & Rewards",
          drawerItemStyle: { display: "none" },
        }}
      />
      <Drawer.Screen
        name="payment"
        options={{
          title: "Payments",
          drawerIcon: ({ color, size }) => (
            <Ionicons name="card" size={size} color={color} />
          ),
          drawerItemStyle: {
            display: !isDriver ? "flex" : "none",
            ...desktopItemStyle,
          },
        }}
      />
      <Drawer.Screen
        name="notifications"
        options={{
          title: desktop ? "Notifications" : "Notification",
          drawerIcon: ({ color, size }) => (
            <Ionicons name="notifications" size={size} color={color} />
          ),
        }}
      />
      <Drawer.Screen
        name="help-center"
        options={{
          title: "Help Center",
          drawerIcon: ({ color, size }) => (
            <Ionicons name="help-circle" size={size} color={color} />
          ),
        }}
      />
      <Drawer.Screen
        name="referral"
        options={{
          title: "Invite a Friend",
          drawerIcon: ({ color, size }) => (
            <Ionicons name="people" size={size} color={color} />
          ),
        }}
      />

      <Drawer.Screen
        name="profile"
        options={{
          drawerItemStyle: { display: "none" },
        }}
      />
    </Drawer>
  );
}

const styles = StyleSheet.create({
  profileHeader: {
    paddingHorizontal: 20,
    paddingBottom: 24,
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: 18,
    fontWeight: "700",
  },
  ratingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 4,
  },
  roleBadge: {
    maxWidth: 80,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  roleText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#fff",
  },
  ratingValue: {
    fontWeight: "600",
  },
  achievementBadges: {
    marginTop: 6,
  },
  ratingLabel: {
    fontSize: 12,
  },
  desktopScene: {
    flex: 1,
  },
  desktopSceneBody: {
    flex: 1,
  },
  homeLabel: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  homeDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: BrandColors.success,
    marginLeft: 8,
  },
});
